import { memo, type ReactNode, useCallback } from 'react';

import { Button } from '@air/primitive-button';
import { Tooltip } from '@air/primitive-tooltip';
import { cn, tw } from '@air/utils-tailwind';

import { SMART_RESIZE_RETRY_HELPER_TEXT } from '../constants';
import { useSmartResizeRetryStore } from '../index';
import { type SmartResizeRetryLastVersion, type SmartResizeRetrySuggestion } from '../types';
import { abbreviatePlatform } from '../utils/abbreviatePlatform';
import { useCollapseLastVersionThumbnails } from '../utils/collapseLastVersionThumbnails';
import { platformIcon } from '../utils/platformIcon';

export type ToolSmartResizeRetryProps = {
  batchLabel?: string;
  lastVersions: SmartResizeRetryLastVersion[];
  /** Absent when the canvas host cannot jump to nodes, which leaves the thumbnails non-interactive. */
  onFocusLastVersion?: (nodeId: string) => void;
  originalImageUrl?: string;
  suggestions: readonly SmartResizeRetrySuggestion[];
};

/**
 * The overflow tile caps the row at four tiles, and 56px is what keeps those four on one line at the
 * narrowest sidebar: 320px less the px-6 here and the pr-3 on the chat's trailing slot leaves 260px,
 * and four 56px tiles plus three 8px gaps need 248px.
 */
const THUMBNAIL_CLASSES = tw('flex w-14 flex-col gap-1');

const THUMBNAIL_FRAME_CLASSES = tw(
  'relative flex h-20 w-14 items-center justify-center overflow-hidden rounded-8 ads:bg-subtle bg-gray-3',
);

/** 20px so the 12px mark's corners clear the circle — its diagonal is 17px, wider than a 16px badge. */
const PLATFORM_BADGE_CLASSES = tw(
  'absolute top-1 right-1 flex h-5 items-center justify-center rounded-full ads:bg-base/90 bg-gray-1/90 ads:text-icon-default text-gray-11',
);

/** Falls back to initials for platforms the icon set does not cover yet (Snap). */
const PlatformBadge = memo(({ platformLabel }: { platformLabel: string }) => {
  const Icon = platformIcon(platformLabel);

  if (!Icon) {
    return (
      <span
        className={cn(PLATFORM_BADGE_CLASSES, 'px-1.5 font-medium text-10')}
        data-testid="SMART_RESIZE_RETRY_PLATFORM_BADGE"
      >
        {abbreviatePlatform(platformLabel)}
      </span>
    );
  }

  return (
    <span className={cn(PLATFORM_BADGE_CLASSES, 'w-5')} data-testid="SMART_RESIZE_RETRY_PLATFORM_BADGE">
      <Icon aria-hidden className="h-3 w-3" data-testid="SMART_RESIZE_RETRY_PLATFORM_ICON" />
    </span>
  );
});

PlatformBadge.displayName = 'PlatformBadge';

type ThumbnailFrameProps = {
  children: ReactNode;
  onFocusNode?: () => void;
};

/** Stays a plain frame when there is no canvas node to jump to, so only actionable tiles look clickable. */
const ThumbnailFrame = memo(({ children, onFocusNode }: ThumbnailFrameProps) =>
  onFocusNode ? (
    <button
      className={cn(THUMBNAIL_FRAME_CLASSES, 'cursor-pointer ads:hover:ring-primary hover:ring-2 hover:ring-blue-9')}
      data-testid="SMART_RESIZE_RETRY_THUMBNAIL_BUTTON"
      onClick={onFocusNode}
      type="button"
    >
      {children}
    </button>
  ) : (
    <div className={THUMBNAIL_FRAME_CLASSES}>{children}</div>
  ),
);

ThumbnailFrame.displayName = 'ThumbnailFrame';

type InterventionThumbnailProps = {
  alt: string;
  imageUrl: string;
  label: string;
  onFocusNode?: () => void;
  platformLabel?: string;
  /** Full "<platform> <format>" name — the caption truncates, so hovering the tile reveals it. */
  tooltip?: string;
};

const InterventionThumbnail = memo(
  ({ alt, imageUrl, label, onFocusNode, platformLabel, tooltip }: InterventionThumbnailProps) => (
    <Tooltip data-testid="SMART_RESIZE_RETRY_THUMBNAIL_TOOLTIP" label={tooltip} side="top">
      <figure className={THUMBNAIL_CLASSES} data-testid="SMART_RESIZE_RETRY_THUMBNAIL">
        <ThumbnailFrame onFocusNode={onFocusNode}>
          {/* biome-ignore lint/performance/noImgElement: presentational thumbnail */}
          <img alt={alt} className="max-h-full max-w-full object-contain" src={imageUrl} />
          {platformLabel ? <PlatformBadge platformLabel={platformLabel} /> : null}
        </ThumbnailFrame>
        <figcaption
          className="truncate ads:text-metadata text-11 text-gray-10"
          data-testid="SMART_RESIZE_RETRY_THUMBNAIL_CAPTION"
        >
          {label}
        </figcaption>
      </figure>
    </Tooltip>
  ),
);

InterventionThumbnail.displayName = 'InterventionThumbnail';

const OverflowThumbnail = memo(({ count }: { count: number }) => (
  <figure className={THUMBNAIL_CLASSES} data-testid="SMART_RESIZE_RETRY_THUMBNAIL">
    <div className="flex h-20 w-14 items-center justify-center rounded-8 ads:bg-subtle bg-gray-3 font-medium ads:text-icon-default text-14 text-gray-11">
      +{count}
    </div>
    <figcaption
      className="truncate ads:text-metadata text-11 text-gray-10"
      data-testid="SMART_RESIZE_RETRY_THUMBNAIL_CAPTION"
    >
      More
    </figcaption>
  </figure>
));

OverflowThumbnail.displayName = 'OverflowThumbnail';

export const ToolSmartResizeRetry = memo(
  ({ batchLabel, lastVersions, onFocusLastVersion, originalImageUrl, suggestions }: ToolSmartResizeRetryProps) => {
    const selectedIds = useSmartResizeRetryStore((state) => state.selectedSuggestionIds);
    const toggleSuggestion = useSmartResizeRetryStore((state) => state.toggleSuggestion);
    const { overflowCount, visible: visibleLastVersions } = useCollapseLastVersionThumbnails(lastVersions);

    const handleToggle = useCallback(
      (id: string) => {
        toggleSuggestion(id);
      },
      [toggleSuggestion],
    );

    const resizeDescription = batchLabel ? `the same ${batchLabel} as last time` : 'the same resize';
    const isSingleLastVersion = lastVersions.length === 1;

    return (
      <div className="z-10 flex w-full shrink-0 gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-2 ads:text-primary text-14 text-gray-12">
          <p>Looks like that&apos;s {resizeDescription} — what didn&apos;t work?</p>

          {(originalImageUrl || lastVersions.length > 0) && (
            <div className="flex flex-wrap items-start gap-2" data-testid="SMART_RESIZE_RETRY_THUMBNAILS">
              {originalImageUrl ? (
                <InterventionThumbnail alt="Original" imageUrl={originalImageUrl} label="Original" />
              ) : null}
              {visibleLastVersions.map((version) => {
                const { nodeId } = version;

                return (
                  <InterventionThumbnail
                    alt={version.formatLabel}
                    imageUrl={version.imageUrl}
                    key={version.formatId}
                    label={isSingleLastVersion ? 'Latest' : version.formatShortLabel}
                    onFocusNode={nodeId && onFocusLastVersion ? () => onFocusLastVersion(nodeId) : undefined}
                    platformLabel={version.platformLabel}
                    tooltip={[version.platformLabel, version.formatLabel].filter(Boolean).join(' ')}
                  />
                );
              })}
              {overflowCount > 0 ? <OverflowThumbnail count={overflowCount} /> : null}
            </div>
          )}

          <div className="flex flex-col items-start gap-1.5">
            {suggestions.map((suggestion, index) => {
              const selected = selectedIds.includes(suggestion.id);

              return (
                <Button
                  appearance={selected ? 'filled' : 'outline'}
                  className="self-start rounded-full"
                  color={selected ? 'blue' : 'gray'}
                  key={suggestion.id}
                  // biome-ignore lint/performance/noJsxPropsBind: per-suggestion handler inside a map; binding the id without a closure needs a dedicated row component
                  onClick={() => handleToggle(suggestion.id)}
                  prefix={
                    <span
                      className={cn(selected ? 'ads:text-inverse/60 text-gray-1/60' : 'ads:text-metadata text-gray-9')}
                    >
                      {index + 1}
                    </span>
                  }
                  size="small"
                >
                  {suggestion.label}
                </Button>
              );
            })}
          </div>

          <p className="ads:text-metadata text-12 text-gray-10">{SMART_RESIZE_RETRY_HELPER_TEXT}</p>
        </div>
      </div>
    );
  },
);

ToolSmartResizeRetry.displayName = 'ToolSmartResizeRetry';
